import { useState, useEffect, useCallback } from 'react';
import { bridgesService, BRIDGE_STATUS_COLORS } from '../../services/bridges.service';
import Modal from '../ui/Modal';
import { supabase } from '../../supabaseClient';
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from 'react-leaflet';

const fmtAgo = (secs) => {
  if (secs == null) return '—';
  if (secs < 60)    return `${secs}s ago`;
  if (secs < 3600)  return `${Math.floor(secs/60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs/3600)}h ago`;
  return `${Math.floor(secs/86400)}d ago`;
};

export default function BridgesPage({ readOnly = false }) {
  const [bridges, setBridges]  = useState([]);
  const [loading, setLoading]  = useState(true);
  const [err,     setErr]      = useState('');
  const [busy,    setBusy]     = useState(null);
  const [toast,   setToast]    = useState('');
  const [search,  setSearch]   = useState('');
  const [filter,  setFilter]   = useState('all'); // all | online | stale | offline
  const [viewMode, setViewMode] = useState('by-org'); // by-org | all | map
  const [selectedOrg, setSelectedOrg] = useState(null); // orgId or null
  const [facilities, setFacilities] = useState([]);   // for map coordinates
  const [detailBridge, setDetailBridge] = useState(null); // full bridge row for detail modal
  const [cmdHistory, setCmdHistory] = useState([]);
  const [cmdLoading, setCmdLoading] = useState(false);
  const [editCfgFor, setEditCfgFor] = useState(null); // bridge whose config we're editing
  const [cfgForm, setCfgForm]       = useState({});
  const [cfgSaving, setCfgSaving]   = useState(false);
  const [orgFacilities, setOrgFacilities] = useState([]); // facilities for the bridge's org
  const [latestRelease, setLatestRelease] = useState(null);
  const [updateModal, setUpdateModal]     = useState(null); // bridge to update
  const [updateUrl, setUpdateUrl]         = useState('');
  const [updatePushing, setUpdatePushing] = useState(false);

  const load = useCallback(async () => {
    try {
      const rows = await bridgesService.listAll();
      setBridges(rows);
      setErr('');
    } catch(e) {
      setErr(e.message || 'Failed to load bridges');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  // Latest bridge release from bridge_releases (used to flag out-of-date bridges)
  useEffect(() => {
    supabase.from('bridge_releases')
      .select('version,channel,platform,download_url,release_notes,released_at')
      .eq('channel', 'stable')
      .order('released_at', { ascending: false })
      .limit(1)
      .then(({ data }) => setLatestRelease(data?.[0] || null));
  }, []);

  // Fetch facility coordinates for the Map view.
  // Refetch whenever Map view is opened + every 30s while it's open,
  // so coord edits on the Facilities page show up without a manual refresh.
  useEffect(() => {
    const fetchFacs = () => {
      supabase.from('facilities').select('id,name,org_id,latitude,longitude').then(({ data }) => {
        setFacilities(data || []);
      });
    };
    fetchFacs();
    if (viewMode !== 'map') return;
    const t = setInterval(fetchFacs, 30_000);
    return () => clearInterval(t);
  }, [viewMode]);

  // Command history — polls every 5s while detail modal is open
  useEffect(() => {
    if (!detailBridge) { setCmdHistory([]); return; }
    let cancel = false;
    const fetchCmds = async () => {
      setCmdLoading(true);
      try {
        const rows = await bridgesService.listCommands(detailBridge.id, 30);
        if (!cancel) setCmdHistory(rows);
      } catch(e) { console.warn('cmd history:', e.message); }
      finally { if (!cancel) setCmdLoading(false); }
    };
    fetchCmds();
    const t = setInterval(fetchCmds, 5000);
    return () => { cancel = true; clearInterval(t); };
  }, [detailBridge]);

  const issue = async (bridge, command) => {
    if (readOnly) return;
    setBusy(`${bridge.id}:${command}`);
    try {
      await bridgesService.issueCommand(bridge.id, command);
      setToast(`✓ ${command} queued for ${bridge.bridge_name}`);
      setTimeout(() => setToast(''), 3000);
    } catch(e) {
      setToast(`✗ ${e.message}`);
      setTimeout(() => setToast(''), 4000);
    } finally {
      setBusy(null);
    }
  };

  const stats = {
    total:   bridges.length,
    online:  bridges.filter(b => b.status === 'online').length,
    stale:   bridges.filter(b => b.status === 'stale').length,
    offline: bridges.filter(b => b.status === 'offline').length,
  };

  // Apply search + status filter
  const q = search.trim().toLowerCase();
  const filtered = bridges.filter(b => {
    if (filter !== 'all' && b.status !== filter) return false;
    if (!q) return true;
    return (
      (b.bridge_name  || '').toLowerCase().includes(q) ||
      (b.org_name     || '').toLowerCase().includes(q) ||
      (b.ip_address   || '').toLowerCase().includes(q) ||
      (b.facility_name|| '').toLowerCase().includes(q) ||
      (b.hostname     || '').toLowerCase().includes(q)
    );
  });

  // Group by org — orgs with any offline bridge first
  const groups = {};
  filtered.forEach(b => {
    const key = b.org_id || 'unknown';
    if (!groups[key]) groups[key] = { orgId: key, orgName: b.org_name || key, bridges: [], counts: { online:0, stale:0, offline:0 } };
    groups[key].bridges.push(b);
    groups[key].counts[b.status] = (groups[key].counts[b.status] || 0) + 1;
  });
  const orderedGroups = Object.values(groups).sort((a, b) => {
    if ((b.counts.offline||0) - (a.counts.offline||0) !== 0) return (b.counts.offline||0) - (a.counts.offline||0);
    return a.orgName.localeCompare(b.orgName);
  });

  return (
    <div style={{ padding:24 }}>
      <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20 }}>
        <div>
          <div style={{ fontFamily:"'Syne',sans-serif",fontWeight:800,fontSize:22,color:'#e8edf2' }}>📡 Bridge Fleet</div>
          <div style={{ fontSize:12,color:'#7090a0',marginTop:4 }}>Monitor and remote-manage all deployed RFID bridges — refreshes every 10s</div>
        </div>
        <div style={{ display:'flex',gap:8,alignItems:'center' }}>
          {/* View mode toggle */}
          <div style={{ display:'inline-flex',background:'#0d1821',border:'1px solid #1a2a3a',borderRadius:8,padding:2 }}>
            {[['by-org','By Org'],['all','All Bridges'],['map','🗺 Map']].map(([v,label])=>{
              const active = viewMode === v;
              return (
                <button key={v} className="btn"
                  onClick={()=>{ setViewMode(v); setSelectedOrg(null); }}
                  style={{ fontSize:11,padding:'6px 12px',borderRadius:6,border:'none',
                    background: active ? '#5b9cf618' : 'transparent',
                    color: active ? '#5b9cf6' : '#7090a0',fontWeight: active ? 700 : 500 }}>
                  {label}
                </button>
              );
            })}
          </div>
          <button className="btn" style={{ fontSize:12,padding:'6px 14px',background:'#1a2a3a',color:'#c8d6e0' }} onClick={load}>↻ Refresh</button>
        </div>
      </div>

      {/* Stat cards — clickable filters */}
      <div style={{ display:'grid',gridTemplateColumns:'repeat(4, 1fr)',gap:14,marginBottom:14 }}>
        {[
          ['all',     'Total',   stats.total,   '#5b9cf6'],
          ['online',  'Online',  stats.online,  BRIDGE_STATUS_COLORS.online],
          ['stale',   'Stale',   stats.stale,   BRIDGE_STATUS_COLORS.stale],
          ['offline', 'Offline', stats.offline, BRIDGE_STATUS_COLORS.offline],
        ].map(([key, label, val, color]) => {
          const active = filter === key;
          return (
            <div key={key} className="card"
              onClick={() => setFilter(key)}
              style={{ padding:16,borderLeft:`3px solid ${color}`,cursor:'pointer',
                background: active ? color+'12' : undefined,
                boxShadow: active ? `0 0 0 1px ${color}60 inset` : undefined }}>
              <div style={{ fontSize:11,color:'#7090a0',textTransform:'uppercase',letterSpacing:'1px' }}>{label}</div>
              <div style={{ fontSize:28,fontWeight:800,color,marginTop:4 }}>{val}</div>
            </div>
          );
        })}
      </div>

      {/* Search + active filter chip */}
      <div style={{ display:'flex',gap:10,marginBottom:16,alignItems:'center' }}>
        <input className="inp" type="text" placeholder="Search by bridge name, org, IP, facility…"
          value={search} onChange={e=>setSearch(e.target.value)}
          style={{ flex:1 }}/>
        {filter !== 'all' && (
          <button className="btn" style={{ fontSize:11,padding:'6px 12px',background:'#1a2a3a',color:'#c8d6e0' }}
            onClick={()=>setFilter('all')}>✕ Clear filter: {filter}</button>
        )}
      </div>

      {toast && (
        <div style={{ marginBottom:14,padding:'10px 14px',background:'#0d1821',border:'1px solid #1a2a3a',borderRadius:8,fontSize:12,color:'#c8d6e0' }}>{toast}</div>
      )}
      {err && (
        <div style={{ marginBottom:14,padding:'10px 14px',background:'#e0575710',border:'1px solid #e0575730',borderRadius:8,fontSize:12,color:'#e05757' }}>⚠ {err}</div>
      )}

      {/* Content */}
      {loading ? (
        <div style={{ padding:40,textAlign:'center',color:'#7090a0' }}>Loading bridges…</div>
      ) : bridges.length === 0 ? (
        <div className="card" style={{ padding:40,textAlign:'center',color:'#7090a0' }}>
          No bridges registered yet. Bridges appear here automatically once they send their first heartbeat.
        </div>
      ) : viewMode === 'map' ? (
        <BridgeMap bridges={filtered} facilities={facilities} onSelect={setDetailBridge}/>
      ) : viewMode === 'by-org' && !selectedOrg ? (
        /* ── Org cards grid ── */
        orderedGroups.length === 0 ? (
          <div className="card" style={{ padding:40,textAlign:'center',color:'#7090a0' }}>No orgs match the current filter.</div>
        ) : (
          <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fill, minmax(280px, 1fr))',gap:14 }}>
            {orderedGroups.map(group => {
              const total = group.bridges.length;
              const primaryColor = group.counts.offline > 0 ? BRIDGE_STATUS_COLORS.offline
                                 : group.counts.stale   > 0 ? BRIDGE_STATUS_COLORS.stale
                                 : BRIDGE_STATUS_COLORS.online;
              return (
                <div key={group.orgId} className="card"
                  onClick={()=>setSelectedOrg(group.orgId)}
                  style={{ padding:18,borderTop:`3px solid ${primaryColor}`,cursor:'pointer',
                    transition:'transform .1s' }}
                  onMouseEnter={e=>{ e.currentTarget.style.transform='translateY(-2px)'; }}
                  onMouseLeave={e=>{ e.currentTarget.style.transform=''; }}>
                  <div style={{ fontSize:15,fontWeight:700,color:'#e8edf2',marginBottom:4 }}>{group.orgName}</div>
                  <div style={{ fontSize:11,color:'#7090a0',marginBottom:14 }}>{total} bridge{total===1?'':'s'}</div>
                  <div style={{ display:'flex',gap:8,flexWrap:'wrap' }}>
                    <div style={{ flex:1,minWidth:70,padding:'8px 10px',background:BRIDGE_STATUS_COLORS.online+'12',border:`1px solid ${BRIDGE_STATUS_COLORS.online}30`,borderRadius:8,textAlign:'center' }}>
                      <div style={{ fontSize:18,fontWeight:800,color:BRIDGE_STATUS_COLORS.online }}>{group.counts.online||0}</div>
                      <div style={{ fontSize:10,color:'#7090a0',marginTop:2 }}>Online</div>
                    </div>
                    <div style={{ flex:1,minWidth:70,padding:'8px 10px',background:BRIDGE_STATUS_COLORS.stale+'12',border:`1px solid ${BRIDGE_STATUS_COLORS.stale}30`,borderRadius:8,textAlign:'center' }}>
                      <div style={{ fontSize:18,fontWeight:800,color:BRIDGE_STATUS_COLORS.stale }}>{group.counts.stale||0}</div>
                      <div style={{ fontSize:10,color:'#7090a0',marginTop:2 }}>Stale</div>
                    </div>
                    <div style={{ flex:1,minWidth:70,padding:'8px 10px',background:BRIDGE_STATUS_COLORS.offline+'12',border:`1px solid ${BRIDGE_STATUS_COLORS.offline}30`,borderRadius:8,textAlign:'center' }}>
                      <div style={{ fontSize:18,fontWeight:800,color:BRIDGE_STATUS_COLORS.offline }}>{group.counts.offline||0}</div>
                      <div style={{ fontSize:10,color:'#7090a0',marginTop:2 }}>Offline</div>
                    </div>
                  </div>
                  <div style={{ marginTop:12,fontSize:11,color:'#5b9cf6',fontWeight:600 }}>View bridges →</div>
                </div>
              );
            })}
          </div>
        )
      ) : (
        /* ── Flat bridge cards (All Bridges OR drilled-into-org) ── */
        (() => {
          const list = selectedOrg
            ? filtered.filter(b => (b.org_id || 'unknown') === selectedOrg)
            : filtered;
          const drilledOrg = selectedOrg ? (groups[selectedOrg]?.orgName || selectedOrg) : null;
          return (
            <>
              {selectedOrg && (
                <div style={{ display:'flex',alignItems:'center',gap:12,marginBottom:14 }}>
                  <button className="btn" style={{ fontSize:11,padding:'6px 12px',background:'#1a2a3a',color:'#c8d6e0' }}
                    onClick={()=>setSelectedOrg(null)}>← Back to orgs</button>
                  <div style={{ fontSize:15,fontWeight:700,color:'#e8edf2' }}>{drilledOrg}</div>
                  <div style={{ fontSize:11,color:'#7090a0' }}>{list.length} bridge{list.length===1?'':'s'}</div>
                </div>
              )}
              {list.length === 0 ? (
                <div className="card" style={{ padding:40,textAlign:'center',color:'#7090a0' }}>No bridges match the current filter.</div>
              ) : (
                <div style={{ display:'grid',gridTemplateColumns:'repeat(auto-fill, minmax(340px, 1fr))',gap:14 }}>
                  {list.map(b => {
                    const c = BRIDGE_STATUS_COLORS[b.status] || '#7090a0';
                    return (
                      <div key={b.id} className="card"
                        onClick={(e)=>{ if (e.target.closest('button')) return; setDetailBridge(b); }}
                        style={{ padding:16,borderTop:`3px solid ${c}`,cursor:'pointer' }}>
                        <div style={{ display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:10 }}>
                          <div>
                            <div style={{ fontSize:14,fontWeight:700,color:'#e8edf2' }}>{b.bridge_name}</div>
                            <div style={{ fontSize:11,color:'#7090a0',marginTop:2 }}>{b.org_name || b.hostname || b.id.slice(0,8)}</div>
                          </div>
                          <span style={{ fontSize:10,fontWeight:700,padding:'3px 8px',borderRadius:12,background:c+'20',color:c,textTransform:'uppercase',letterSpacing:'.5px' }}>
                            ● {b.status}
                          </span>
                        </div>
                        <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:6,fontSize:11,marginBottom:12 }}>
                          <div><span style={{ color:'#7090a0' }}>Platform:</span> <span style={{ color:'#c8d6e0' }}>{b.platform || '—'}</span></div>
                          <div><span style={{ color:'#7090a0' }}>Version:</span> <span style={{ color:'#c8d6e0',fontFamily:"'IBM Plex Mono',monospace" }}>{b.version || '—'}</span></div>
                          <div><span style={{ color:'#7090a0' }}>IP:</span> <span style={{ color:'#c8d6e0',fontFamily:"'IBM Plex Mono',monospace" }}>{b.ip_address || '—'}</span></div>
                          <div><span style={{ color:'#7090a0' }}>Facility:</span> <span style={{ color:'#c8d6e0' }}>{b.facility_name || '—'}</span></div>
                          <div><span style={{ color:'#7090a0' }}>Readers:</span> <span style={{ color:'#c8d6e0' }}>{b.reader_count ?? 0}</span></div>
                          <div><span style={{ color:'#7090a0' }}>Tags:</span> <span style={{ color:'#c8d6e0' }}>{b.tags_total ?? 0}</span></div>
                          <div style={{ gridColumn:'1/-1' }}>
                            <span style={{ color:'#7090a0' }}>Last heartbeat:</span> <span style={{ color:c }}>{fmtAgo(b.seconds_since_heartbeat)}</span>
                          </div>
                        </div>
                        {!readOnly && (
                          <div style={{ display:'flex',gap:6 }}>
                            <button className="btn" style={{ flex:1,fontSize:11,padding:'6px 8px',background:'#5b9cf618',border:'1px solid #5b9cf640',color:'#5b9cf6' }}
                              disabled={busy===`${b.id}:ping`}
                              onClick={()=>issue(b,'ping')}>🏓 Ping</button>
                            <button className="btn" style={{ flex:1,fontSize:11,padding:'6px 8px',background:'#f5a62318',border:'1px solid #f5a62340',color:'#f5a623' }}
                              disabled={busy===`${b.id}:restart`}
                              onClick={()=>issue(b,'restart')}>🔄 Restart</button>
                            <button className="btn" style={{ flex:1,fontSize:11,padding:'6px 8px',background:'#00e5a018',border:'1px solid #00e5a040',color:'#00e5a0' }}
                              disabled={busy===`${b.id}:reload_config`}
                              onClick={()=>issue(b,'reload_config')}>⚙ Reload</button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          );
        })()
      )}

      {/* ── Bridge detail + command history modal ── */}
      {detailBridge && (
        <Modal title={`📡 ${detailBridge.bridge_name}`} onClose={()=>setDetailBridge(null)} width={720}>
          {(() => {
            const c = BRIDGE_STATUS_COLORS[detailBridge.status] || '#7090a0';
            return (
              <>
                <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14 }}>
                  <div style={{ fontSize:12,color:'#7090a0' }}>{detailBridge.org_name || detailBridge.org_id}</div>
                  <span style={{ fontSize:10,fontWeight:700,padding:'3px 10px',borderRadius:12,background:c+'20',color:c,textTransform:'uppercase',letterSpacing:'.5px' }}>
                    ● {detailBridge.status}
                  </span>
                </div>

                {/* Info grid */}
                <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8,marginBottom:18 }}>
                  {[
                    ['Bridge ID',   detailBridge.id],
                    ['Hostname',    detailBridge.hostname],
                    ['Platform',    detailBridge.platform],
                    ['Version',     latestRelease && detailBridge.version && latestRelease.version !== detailBridge.version ? `${detailBridge.version}  (latest: ${latestRelease.version})` : detailBridge.version],
                    ['IP Address',  detailBridge.ip_address],
                    ['Facility',    detailBridge.facility_name],
                    ['Readers',     detailBridge.reader_count ?? 0],
                    ['Tags Total',  detailBridge.tags_total ?? 0],
                    ['Last Heartbeat', fmtAgo(detailBridge.seconds_since_heartbeat)],
                  ].map(([k,v]) => (
                    <div key={k} style={{ background:'#060b10',border:'1px solid #1a2a3a',borderRadius:8,padding:'8px 10px' }}>
                      <div style={{ fontSize:9,color:'#7090a0',textTransform:'uppercase',letterSpacing:'1px' }}>{k}</div>
                      <div style={{ fontSize:12,color:'#c8d6e0',marginTop:2,fontFamily: k==='Bridge ID'||k==='IP Address'||k==='Version' ? "'IBM Plex Mono',monospace" : 'inherit',
                        overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }} title={v}>{v || '—'}</div>
                    </div>
                  ))}
                </div>

                {/* Command actions */}
                {!readOnly && (
                  <div style={{ display:'flex',gap:6,marginBottom:18,flexWrap:'wrap' }}>
                    <button className="btn" style={{ flex:1,minWidth:100,fontSize:12,padding:'8px',background:'#5b9cf618',border:'1px solid #5b9cf640',color:'#5b9cf6' }}
                      onClick={()=>issue(detailBridge,'ping')}>🏓 Ping</button>
                    <button className="btn" style={{ flex:1,minWidth:100,fontSize:12,padding:'8px',background:'#f5a62318',border:'1px solid #f5a62340',color:'#f5a623' }}
                      onClick={()=>issue(detailBridge,'restart')}>🔄 Restart</button>
                    <button className="btn" style={{ flex:1,minWidth:100,fontSize:12,padding:'8px',background:'#00e5a018',border:'1px solid #00e5a040',color:'#00e5a0' }}
                      onClick={()=>issue(detailBridge,'reload_config')}>⚙ Reload</button>
                    <button className="btn" style={{ flex:1,minWidth:100,fontSize:12,padding:'8px',background:'#5b9cf618',border:'1px solid #5b9cf640',color:'#5b9cf6' }}
                      onClick={()=>issue(detailBridge,'scan_now')}>🔍 Scan Now</button>
                    {latestRelease && latestRelease.version !== detailBridge.version && (
                      <button className="btn" style={{ flex:1,minWidth:120,fontSize:12,padding:'8px',background:'#e0575720',border:'1px solid #e0575760',color:'#e05757',fontWeight:700 }}
                        onClick={()=>{ setUpdateUrl(latestRelease.download_url || ''); setUpdateModal(detailBridge); }}>
                        ⬆ Update to v{latestRelease.version}
                      </button>
                    )}
                    <button className="btn" style={{ flex:1,minWidth:100,fontSize:12,padding:'8px',background:'#b89de818',border:'1px solid #b89de840',color:'#b89de8' }}
                      onClick={()=>{
                        setCfgForm({
                          bridge_name:   detailBridge.bridge_name || '',
                          facility_id:   detailBridge.facility_id || '',
                          facility_name: detailBridge.facility_name || '',
                          reader_1_name: (detailBridge.config_snapshot?.readers?.[0]?.name) || '',
                          reader_1_ip:   (detailBridge.config_snapshot?.readers?.[0]?.ip)   || '',
                          reader_1_port: (detailBridge.config_snapshot?.readers?.[0]?.port) || 9090,
                          reader_1_power: 30,
                        });
                        setEditCfgFor(detailBridge);
                        // Load facilities for this bridge's org (works because SuperAdmin bypasses RLS)
                        supabase.from('facilities').select('id,name').eq('org_id', detailBridge.org_id).then(({ data }) => setOrgFacilities(data || []));
                      }}>✏ Edit Config</button>
                  </div>
                )}

                {/* Command history */}
                <div style={{ fontSize:13,fontWeight:700,color:'#c8d6e0',marginBottom:10,display:'flex',alignItems:'center',gap:8 }}>
                  📋 Command History
                  {cmdLoading && <span style={{ fontSize:10,color:'#7090a0',fontWeight:400 }}>updating…</span>}
                </div>
                {cmdHistory.length === 0 ? (
                  <div style={{ padding:20,textAlign:'center',color:'#7090a0',fontSize:12,background:'#060b10',border:'1px solid #1a2a3a',borderRadius:8 }}>
                    No commands issued yet
                  </div>
                ) : (
                  <div style={{ background:'#060b10',border:'1px solid #1a2a3a',borderRadius:8,overflow:'hidden',maxHeight:280,overflowY:'auto' }}>
                    <table style={{ width:'100%',borderCollapse:'collapse',fontSize:11 }}>
                      <thead style={{ position:'sticky',top:0,background:'#0d1821' }}>
                        <tr>
                          {['Command','Issued','Status','Duration','Result'].map(h => (
                            <th key={h} style={{ textAlign:'left',padding:'8px 10px',fontSize:9,color:'#7090a0',textTransform:'uppercase',letterSpacing:'1px',borderBottom:'1px solid #1a2a3a' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {cmdHistory.map(cmd => {
                          const statusColor = cmd.status === 'completed' ? '#00e5a0'
                                            : cmd.status === 'failed'    ? '#e05757'
                                            : cmd.status === 'picked_up' ? '#5b9cf6'
                                            : '#f5a623';
                          const dur = cmd.completed_at && cmd.issued_at
                            ? Math.max(0, Math.round((new Date(cmd.completed_at) - new Date(cmd.issued_at))/1000)) + 's'
                            : cmd.picked_up_at && !cmd.completed_at ? 'running…' : '—';
                          const resultText = cmd.result ? (typeof cmd.result === 'object' ? JSON.stringify(cmd.result) : String(cmd.result)) : '—';
                          return (
                            <tr key={cmd.id} style={{ borderBottom:'1px solid #0d1821' }}>
                              <td style={{ padding:'8px 10px',fontFamily:"'IBM Plex Mono',monospace",color:'#c8d6e0' }}>{cmd.command}</td>
                              <td style={{ padding:'8px 10px',color:'#a0b4c0' }}>{new Date(cmd.issued_at).toLocaleString('en-MY')}</td>
                              <td style={{ padding:'8px 10px' }}>
                                <span style={{ fontSize:10,fontWeight:700,padding:'2px 8px',borderRadius:10,background:statusColor+'20',color:statusColor,textTransform:'uppercase' }}>
                                  {cmd.status}
                                </span>
                              </td>
                              <td style={{ padding:'8px 10px',color:'#a0b4c0',fontFamily:"'IBM Plex Mono',monospace" }}>{dur}</td>
                              <td style={{ padding:'8px 10px',color:'#7090a0',fontFamily:"'IBM Plex Mono',monospace",fontSize:10,maxWidth:260,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }} title={resultText}>{resultText}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            );
          })()}
        </Modal>
      )}

      {/* ── Update Bridge modal ── */}
      {updateModal && (
        <Modal title={`⬆ Update Bridge — ${updateModal.bridge_name}`} onClose={() => setUpdateModal(null)} width={520}>
          <div style={{ display:'flex',flexDirection:'column',gap:14 }}>
            <div style={{ padding:'10px 14px',background:'#e0575710',border:'1px solid #e0575730',borderRadius:8,fontSize:12,color:'#e05757',lineHeight:1.5 }}>
              Bridge downloads the new JAR from the URL below, saves it as <code style={{ background:'#000',padding:'1px 4px',borderRadius:3 }}>trekify-bridge-update.jar</code>, and swaps it on the next restart (run.sh handles that automatically).
            </div>

            <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,fontSize:12 }}>
              <div style={{ background:'#060b10',border:'1px solid #1a2a3a',borderRadius:8,padding:'8px 10px' }}>
                <div style={{ fontSize:9,color:'#7090a0',textTransform:'uppercase',letterSpacing:'1px' }}>Current Version</div>
                <div style={{ fontFamily:"'IBM Plex Mono',monospace",color:'#c8d6e0',marginTop:2 }}>{updateModal.version || '—'}</div>
              </div>
              <div style={{ background:'#060b10',border:'1px solid #00e5a030',borderRadius:8,padding:'8px 10px' }}>
                <div style={{ fontSize:9,color:'#7090a0',textTransform:'uppercase',letterSpacing:'1px' }}>Latest Release</div>
                <div style={{ fontFamily:"'IBM Plex Mono',monospace",color:'#00e5a0',marginTop:2 }}>{latestRelease?.version || 'n/a'}</div>
              </div>
            </div>

            {latestRelease?.release_notes && (
              <div>
                <div style={{ fontSize:11,fontWeight:700,color:'#c8d6e0',marginBottom:6 }}>Release Notes</div>
                <div style={{ background:'#060b10',border:'1px solid #1a2a3a',borderRadius:8,padding:10,fontSize:11,color:'#a0b4c0',whiteSpace:'pre-wrap',maxHeight:120,overflowY:'auto' }}>{latestRelease.release_notes}</div>
              </div>
            )}

            <div>
              <div style={{ fontSize:10,color:'#7090a0',textTransform:'uppercase',letterSpacing:'1px',marginBottom:4 }}>Download URL</div>
              <input className="inp" value={updateUrl} onChange={e=>setUpdateUrl(e.target.value)} placeholder="https://…/trekify-bridge.jar" style={{ fontFamily:"'IBM Plex Mono',monospace",fontSize:11 }}/>
              <div style={{ fontSize:10,color:'#7090a0',marginTop:4 }}>Direct link to the new JAR file. Bridge must be able to reach this URL.</div>
            </div>
          </div>

          <div style={{ display:'flex',gap:10,marginTop:20 }}>
            <button className="btn" style={{ flex:1,background:'none',border:'1px solid #1a2a3a',color:'#a0b4c0' }} onClick={()=>setUpdateModal(null)}>Cancel</button>
            <button className="btn" style={{ flex:2,background:'#e05757',color:'#fff',fontWeight:700 }} disabled={updatePushing || !updateUrl}
              onClick={async ()=>{
                setUpdatePushing(true);
                try {
                  await bridgesService.issueCommand(updateModal.id, 'update', { url: updateUrl });
                  setToast(`✓ Update queued — bridge will download & stage. Restart to apply.`);
                  setTimeout(() => setToast(''), 5000);
                  setUpdateModal(null);
                } catch(e) {
                  setToast(`✗ ${e.message}`);
                  setTimeout(() => setToast(''), 4000);
                } finally { setUpdatePushing(false); }
              }}>
              {updatePushing ? 'Sending…' : '⬆ Push Update'}
            </button>
          </div>
        </Modal>
      )}

      {/* ── Edit Config modal — remote-push config values to the bridge ── */}
      {editCfgFor && (
        <Modal title={`✏ Edit Config — ${editCfgFor.bridge_name}`} onClose={() => setEditCfgFor(null)} width={520}>
          <div style={{ padding:'10px 12px',background:'#5b9cf610',border:'1px solid #5b9cf630',borderRadius:8,fontSize:11,color:'#5b9cf6',marginBottom:14 }}>
            Changes are pushed to the bridge via a remote command. Readers auto-restart with the new config within ~10 seconds.
          </div>

          <div style={{ display:'flex',flexDirection:'column',gap:12 }}>
            <div>
              <div style={{ fontSize:10,color:'#7090a0',textTransform:'uppercase',letterSpacing:'1px',marginBottom:4 }}>Bridge Name</div>
              <input className="inp" value={cfgForm.bridge_name || ''} onChange={e=>setCfgForm(f=>({...f,bridge_name:e.target.value}))}/>
            </div>

            <div>
              <div style={{ fontSize:10,color:'#7090a0',textTransform:'uppercase',letterSpacing:'1px',marginBottom:4 }}>Facility</div>
              <select className="sel" style={{ width:'100%' }} value={cfgForm.facility_id || ''} onChange={e=>{
                const fac = orgFacilities.find(f => f.id === e.target.value);
                setCfgForm(f=>({...f, facility_id:e.target.value, facility_name: fac?.name || ''}));
              }}>
                <option value="">— Select a facility —</option>
                {orgFacilities.map(fac => <option key={fac.id} value={fac.id}>{fac.name}</option>)}
              </select>
              {cfgForm.facility_id && <div style={{ fontSize:10,color:'#7090a0',marginTop:4,fontFamily:"'IBM Plex Mono',monospace" }}>{cfgForm.facility_id}</div>}
            </div>

            <div style={{ borderTop:'1px solid #1a2a3a',paddingTop:12,marginTop:4 }}>
              <div style={{ fontSize:11,fontWeight:700,color:'#c8d6e0',marginBottom:8 }}>Reader 1</div>
              <div style={{ display:'grid',gridTemplateColumns:'2fr 1fr',gap:8,marginBottom:8 }}>
                <div>
                  <div style={{ fontSize:10,color:'#7090a0',textTransform:'uppercase',marginBottom:4 }}>Name</div>
                  <input className="inp" value={cfgForm.reader_1_name || ''} onChange={e=>setCfgForm(f=>({...f,reader_1_name:e.target.value}))}/>
                </div>
                <div>
                  <div style={{ fontSize:10,color:'#7090a0',textTransform:'uppercase',marginBottom:4 }}>Power (dBm)</div>
                  <input className="inp" type="number" min="10" max="33" value={cfgForm.reader_1_power || 30} onChange={e=>setCfgForm(f=>({...f,reader_1_power:Number(e.target.value)}))}/>
                </div>
              </div>
              <div style={{ display:'grid',gridTemplateColumns:'2fr 1fr',gap:8 }}>
                <div>
                  <div style={{ fontSize:10,color:'#7090a0',textTransform:'uppercase',marginBottom:4 }}>IP Address</div>
                  <input className="inp" value={cfgForm.reader_1_ip || ''} placeholder="192.168.1.201" onChange={e=>setCfgForm(f=>({...f,reader_1_ip:e.target.value}))}/>
                </div>
                <div>
                  <div style={{ fontSize:10,color:'#7090a0',textTransform:'uppercase',marginBottom:4 }}>Port</div>
                  <input className="inp" type="number" value={cfgForm.reader_1_port || 9090} onChange={e=>setCfgForm(f=>({...f,reader_1_port:Number(e.target.value)}))}/>
                </div>
              </div>
            </div>
          </div>

          <div style={{ display:'flex',gap:10,marginTop:20 }}>
            <button className="btn" style={{ flex:1,background:'none',border:'1px solid #1a2a3a',color:'#a0b4c0' }} onClick={()=>setEditCfgFor(null)}>Cancel</button>
            <button className="btn" style={{ flex:2,background:'#b89de8',color:'#060b10',fontWeight:700 }} disabled={cfgSaving}
              onClick={async ()=>{
                setCfgSaving(true);
                try {
                  await bridgesService.issueCommand(editCfgFor.id, 'set_config', cfgForm);
                  setToast(`✓ Config pushed — bridge will apply within 10s`);
                  setTimeout(() => setToast(''), 4000);
                  setEditCfgFor(null);
                } catch(e) {
                  setToast(`✗ ${e.message}`);
                  setTimeout(() => setToast(''), 4000);
                } finally { setCfgSaving(false); }
              }}>
              {cfgSaving ? 'Pushing…' : '📤 Push Config to Bridge'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─── Bridge Fleet Map ─────────────────────────────────────────────────── */
function BridgeMap({ bridges, facilities, onSelect }) {
  const facById = new Map(facilities.map(f => [String(f.id), f]));

  // Join bridges with their facility coords; skip any without valid coords
  const pins = bridges.map(b => {
    const fac = facById.get(String(b.facility_id));
    if (!fac || fac.latitude == null || fac.longitude == null) return null;
    return { ...b, lat: Number(fac.latitude), lng: Number(fac.longitude), facName: fac.name };
  }).filter(Boolean);

  const skipped = bridges.length - pins.length;

  // Default center: Malaysia (KL). Auto-fit to pins if any.
  const defaultCenter = [3.139003, 101.686855];
  const defaultZoom   = 6;

  return (
    <div>
      {skipped > 0 && (
        <div style={{ marginBottom:10,padding:'8px 12px',background:'#f5a62312',border:'1px solid #f5a62330',borderRadius:8,fontSize:11,color:'#f5a623' }}>
          ⚠ {skipped} bridge{skipped===1?'':'s'} not shown — their facility has no latitude/longitude set. Edit the facility on the Facilities page to pin its location.
        </div>
      )}
      <div style={{ height: 560, borderRadius:12, overflow:'hidden', border:'1px solid #1a2a3a' }}>
        <MapContainer center={defaultCenter} zoom={defaultZoom} style={{ height:'100%',width:'100%',background:'#0d1520' }}>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
            url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
            subdomains="abcd" maxZoom={20}
          />
          <FitToPins pins={pins}/>
          {pins.map(b => {
            const c = BRIDGE_STATUS_COLORS[b.status] || '#7090a0';
            return (
              <CircleMarker key={b.id} center={[b.lat, b.lng]}
                pathOptions={{ color: c, fillColor: c, fillOpacity: 0.75, weight: 2 }}
                radius={12}>
                <Popup>
                  <div style={{ fontFamily:'system-ui,sans-serif',minWidth:200 }}>
                    <div style={{ fontWeight:700,fontSize:14,marginBottom:4 }}>{b.bridge_name}</div>
                    <div style={{ fontSize:11,color:'#666',marginBottom:8 }}>{b.org_name || b.org_id}</div>
                    <div style={{ fontSize:11,marginBottom:2 }}>📍 <b>{b.facName}</b></div>
                    <div style={{ fontSize:11,marginBottom:2 }}>Status: <span style={{ color: c,fontWeight:700 }}>● {b.status}</span></div>
                    <div style={{ fontSize:11,marginBottom:2 }}>Readers: {b.reader_count ?? 0}</div>
                    <div style={{ fontSize:11,marginBottom:8 }}>Tags: {(b.tags_total ?? 0).toLocaleString()}</div>
                    <button onClick={() => onSelect(b)}
                      style={{ width:'100%',padding:'6px 10px',background:'#5b9cf6',color:'#fff',border:'none',borderRadius:6,cursor:'pointer',fontSize:11,fontWeight:600 }}>
                      Open details →
                    </button>
                  </div>
                </Popup>
              </CircleMarker>
            );
          })}
        </MapContainer>
      </div>
    </div>
  );
}

function FitToPins({ pins }) {
  const map = useMap();
  useEffect(() => {
    if (pins.length === 0) return;
    if (pins.length === 1) { map.setView([pins[0].lat, pins[0].lng], 13); return; }
    const bounds = pins.map(p => [p.lat, p.lng]);
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
  }, [pins, map]);
  return null;
}
